'use client';

import { formatDateTime, formatRelativeDate } from '@/lib/utils/formatting';
import { Avatar, Chip, Spinner } from '@heroui/react';
import { Icon } from '@iconify/react';
import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import {
    CHAT_STATES,
    DEFAULT_MESSAGES,
    ROLE_TYPES,
    STATUS_MESSAGES,
    STREAMING_EVENT_TYPES,
} from '../constants';
import { useChunkedReveal } from '../hooks/useChunkedReveal';
import type { MessageProps } from '../props';
import type { ChatMessage, ChatMessageContent, TokenUsage } from '../types';
import { renderMessageContent } from '../utils';
import { isExportInProgress, startQueryDataExport } from '../utils/export-manager';
import { CrosstabPicker } from './planner-mode/crosstab-picker';
import { DarkTooltip } from './dark-tooltip';
import { MessageActions } from './message-actions';
import { OptionChips } from './option-chips';
import { ShowMoreDetails } from './show-more-details';
import { StatusMessage } from './status-message';

/** True when this message carries an `agent_options` content block from the agent. */
function hasAgentOptionsContent(message: ChatMessage): boolean {
    if (!Array.isArray(message.content)) return false;
    return (message.content as ChatMessageContent[]).some(
        (c) => c.type === STREAMING_EVENT_TYPES.AGENT_OPTIONS,
    );
}

export const Message = ({
    message,
    user,
    chatState,
    isLatestAssistantMessage,
    className = '',
    currentConversationId,
    currentThreadId,
    showFeedbackSubmit = true,
    submitFeedback,
    allUserFeedback = [],
    cautionToolNames = [],
    onStartNewChatFromMessage,
    messageMarkerProps,
    onAnimationComplete,
    onSendMessage,
    pendingOptions,
    onSelectOption,
    onSelectMultiple,
    onSkipOptions,
    isCrosstabStep,
    crosstabSearchHint,
}: MessageProps) => {
    const existingFeedback = useMemo(() => {
        return allUserFeedback?.find(
            (f) => f.messageId === message.id && f.conversationId === currentConversationId,
        );
    }, [allUserFeedback, message.id, currentConversationId]);

    const handleExportFullData = useCallback(() => {
        if (!currentConversationId || !message.id || isExportInProgress()) return;
        startQueryDataExport(currentConversationId, message.id);
    }, [currentConversationId, message.id]);

    const { thinkingContent, regularContent } = useMemo(
        () =>
            renderMessageContent(message, user, {
                onExportFullData: handleExportFullData,
                onSendMessage,
            }),
        [message, user, handleExportFullData, onSendMessage],
    );

    const handleRevealComplete = useCallback(() => {
        onAnimationComplete?.(message.id);
    }, [onAnimationComplete, message.id]);

    const { content: visibleContent, isRevealing } = useChunkedReveal(regularContent, {
        enabled: message.role === ROLE_TYPES.ASSISTANT && !message.isStreaming,
        isLatest: !!isLatestAssistantMessage,
        messageId: message.id,
        animate: !!message._animate,
        onRevealComplete: handleRevealComplete,
    });

    const revealWrapperRef = useRef<HTMLDivElement>(null);
    const prevHeightRef = useRef(0);

    useLayoutEffect(() => {
        const el = revealWrapperRef.current;
        if (!el) return;

        const newHeight = el.scrollHeight;
        const prevHeight = prevHeightRef.current;
        prevHeightRef.current = newHeight;

        if (prevHeight === 0 || prevHeight === newHeight) {
            if (!isRevealing) el.removeAttribute('style');
            return;
        }

        el.style.transition = 'none';
        el.style.height = `${prevHeight}px`;
        el.style.overflow = 'hidden';
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        el.offsetHeight; // force reflow
        el.style.transition = 'height 300ms ease-out';
        el.style.height = `${newHeight}px`;

        let onTransitionEnd: (() => void) | null = null;

        if (!isRevealing) {
            onTransitionEnd = () => {
                el.removeAttribute('style');
                el.removeEventListener('transitionend', onTransitionEnd!);
                onTransitionEnd = null;
            };
            el.addEventListener('transitionend', onTransitionEnd);
        }

        return () => {
            if (onTransitionEnd) {
                el.removeEventListener('transitionend', onTransitionEnd);
                el.removeAttribute('style');
            }
        };
    }, [visibleContent, isRevealing]);

    const hasExportableQuery = useMemo(() => {
        if (!Array.isArray(message.content)) return false;
        for (const c of message.content as ChatMessageContent[]) {
            if (c.table?.query_id) return true;
            if (c.tool_result?.content) {
                for (const inner of c.tool_result.content) {
                    const qid = (inner.json as Record<string, unknown> | undefined)?.query_id;
                    if (typeof qid === 'string') return true;
                }
            }
        }
        return false;
    }, [message.content]);

    // Skip messages that only contain search tool use without text
    if (
        message.role === ROLE_TYPES.ASSISTANT &&
        Array.isArray(message.content) &&
        message.content.length === 2 &&
        message.content[0]?.type === STREAMING_EVENT_TYPES.TOOL_USE &&
        message.content[0]?.tool_use?.name === 'search1' &&
        message.content[1]?.type === STREAMING_EVENT_TYPES.TOOL_RESULT
    ) {
        return null;
    }

    // Streaming assistant messages are not rendered here; the StatusMessage handles them
    if (message.isStreaming && message.role === ROLE_TYPES.ASSISTANT) {
        return null;
    }

    // Render option-selected user messages as a compact chip
    const isOptionSelection = message.metadata?.isOptionSelection === true;
    const optionLabel =
        isOptionSelection && message.metadata?.option_selected
            ? (message.metadata.option_selected as { label?: string }).label
            : null;

    if (message.role === ROLE_TYPES.USER && isOptionSelection && optionLabel) {
        return (
            <motion.div
                className={`group/message w-full px-4 py-2 ${className}`}
                initial={{ y: 5, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                data-role="user"
            >
                <div className="ml-auto flex w-fit flex-col items-end gap-1">
                    <Chip
                        variant="flat"
                        startContent={
                            <Icon
                                icon="lucide:check"
                                width={12}
                                height={12}
                                className="text-success-600"
                            />
                        }
                        classNames={{
                            base: 'bg-default-100 dark:bg-default-50 border border-default-200/60 h-auto py-1.5 px-3',
                            content: 'text-small font-medium text-foreground/80 px-1',
                        }}
                    >
                        {optionLabel}
                    </Chip>
                    {message.timestamp && (
                        <DarkTooltip content={formatDateTime(message.timestamp)} size="sm">
                            <span className="w-fit cursor-default select-none text-small text-foreground/40 opacity-0 transition-opacity group-hover/message:opacity-100">
                                {formatRelativeDate(message.timestamp)}
                            </span>
                        </DarkTooltip>
                    )}
                </div>
            </motion.div>
        );
    }

    // Extract tool result names from message content
    const toolResultNames = Array.isArray(message.content)
        ? message.content
              .filter(
                  (
                      content,
                  ): content is ChatMessageContent & {
                      tool_result: NonNullable<ChatMessageContent['tool_result']>;
                  } =>
                      content.type === STREAMING_EVENT_TYPES.TOOL_RESULT &&
                      content.tool_result != null,
              )
              .map((content) => content.tool_result.name)
        : [];

    // Check if any tool result names match the caution tool names from active DANI config
    const shouldShowCautionMessage = toolResultNames.some((name) =>
        cautionToolNames.includes(name),
    );

    return (
        <AnimatePresence>
            <div className="flex flex-col">
                {/* Render all content with avatar */}
                {regularContent.length > 0 && (
                    <motion.div
                        className={`group/message w-full px-4 py-3 ${className}`}
                        initial={{ y: 5, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        data-role={message.role}
                    >
                        <div
                            className={`flex w-full items-start gap-4 ${
                                message.role === ROLE_TYPES.USER
                                    ? 'ml-auto w-fit max-w-lg flex-row-reverse'
                                    : ''
                            }`}
                        >
                            <div className="flex min-w-0 flex-1 flex-col gap-2">
                                <div ref={revealWrapperRef} className="flex min-w-0 flex-col gap-2">
                                    {user?.isInternal &&
                                        thinkingContent.length > 0 &&
                                        message.role === ROLE_TYPES.ASSISTANT && (
                                            <div className="flex flex-col gap-1.5">
                                                {thinkingContent}
                                            </div>
                                        )}
                                    {visibleContent}
                                    {message.role === ROLE_TYPES.ASSISTANT &&
                                        isLatestAssistantMessage &&
                                        isCrosstabStep && (
                                            <CrosstabPicker
                                                defaultSearch={crosstabSearchHint}
                                                onConfirm={(ct, summary) => {
                                                    // Match the planner_step_fields format that PlannerCard uses
                                                    // so DANI receives the same payload regardless of render path.
                                                    // For uploads, summary.preBuiltContext is already serialised —
                                                    // include it so chat-messages.tsx skips the loadData API call.
                                                    onSendMessage?.(
                                                        JSON.stringify({
                                                            action: 'planner_step_fields',
                                                            fields: {
                                                                crosstab_id: ct.id,
                                                                crosstab_name: ct.name,
                                                                ...(summary?.preBuiltContext && {
                                                                    crosstab_data:
                                                                        summary.preBuiltContext,
                                                                }),
                                                            },
                                                            card_index: 0,
                                                        }),
                                                    );
                                                }}
                                            />
                                        )}
                                    {message.role === ROLE_TYPES.ASSISTANT &&
                                        isLatestAssistantMessage &&
                                        !isCrosstabStep &&
                                        pendingOptions &&
                                        hasAgentOptionsContent(message) && (
                                            <AnimatePresence>
                                                <OptionChips
                                                    pendingOptions={pendingOptions}
                                                    onSelectOption={onSelectOption}
                                                    onSelectMultiple={onSelectMultiple}
                                                    onSkipOptions={onSkipOptions}
                                                />
                                            </AnimatePresence>
                                        )}
                                    <AnimatePresence>
                                        {isRevealing && (
                                            <motion.div
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                exit={{ opacity: 0, scale: 0.9 }}
                                                transition={{ duration: 0.2 }}
                                                className="flex items-center gap-2 py-1"
                                            >
                                                <div className="relative flex flex-shrink-0 items-center justify-center">
                                                    <Spinner
                                                        size="sm"
                                                        color="success"
                                                        classNames={{
                                                            wrapper: 'h-8 w-8',
                                                        }}
                                                    />
                                                    <Icon
                                                        icon="lucide:bot"
                                                        width={16}
                                                        height={16}
                                                        className="absolute text-default-600"
                                                    />
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>

                                {message.role === ROLE_TYPES.USER && message.timestamp && (
                                    <div className="flex justify-end">
                                        <DarkTooltip
                                            content={formatDateTime(message.timestamp)}
                                            size="sm"
                                        >
                                            <span className="cursor-default select-none text-small text-foreground/40 opacity-0 transition-opacity group-hover/message:opacity-100">
                                                {formatRelativeDate(message.timestamp)}
                                            </span>
                                        </DarkTooltip>
                                    </div>
                                )}

                                {message.role === ROLE_TYPES.ASSISTANT &&
                                    chatState === CHAT_STATES.EXECUTING_SQL &&
                                    isLatestAssistantMessage && (
                                        <StatusMessage
                                            message={STATUS_MESSAGES.EXECUTING_SQL.message}
                                            isInternal={user?.isInternal}
                                        />
                                    )}

                                {message.role === ROLE_TYPES.ASSISTANT &&
                                    chatState === CHAT_STATES.RUNNING_ANALYTICS &&
                                    isLatestAssistantMessage && (
                                        <StatusMessage
                                            message={STATUS_MESSAGES.RUNNING_ANALYTICS.message}
                                            isInternal={user?.isInternal}
                                        />
                                    )}
                                {user?.isInternal &&
                                    message.role === ROLE_TYPES.ASSISTANT &&
                                    !message.isStreaming &&
                                    Array.isArray(message.content) && (
                                        <ShowMoreDetails
                                            content={message.content as ChatMessageContent[]}
                                            metadata={message.metadata as TokenUsage}
                                        />
                                    )}
                                {shouldShowCautionMessage &&
                                    message.role === ROLE_TYPES.ASSISTANT && (
                                        <div className="mt-1 flex items-center gap-2.5 rounded-lg border border-warning-200/40 bg-warning-50/20 px-3 py-2">
                                            <p className="text-xs leading-snug text-foreground/50">
                                                {DEFAULT_MESSAGES.INFO_WARNING}
                                            </p>
                                            <Chip
                                                size="sm"
                                                color="warning"
                                                variant="flat"
                                                classNames={{
                                                    base: 'h-5 flex-shrink-0',
                                                    content:
                                                        'px-1.5 text-[10px] font-semibold uppercase tracking-wider',
                                                }}
                                            >
                                                Beta
                                            </Chip>
                                        </div>
                                    )}

                                {message.role === ROLE_TYPES.ASSISTANT &&
                                    currentConversationId &&
                                    message.id && (
                                        <>
                                            <MessageActions
                                                messageContent={message.content}
                                                showFeedback={
                                                    showFeedbackSubmit &&
                                                    currentConversationId != null &&
                                                    currentThreadId != null
                                                }
                                                existingFeedback={existingFeedback}
                                                messageId={message.id}
                                                currentConversationId={currentConversationId!}
                                                currentThreadId={currentThreadId!}
                                                submitFeedback={submitFeedback}
                                                startNewChat={
                                                    onStartNewChatFromMessage
                                                        ? () =>
                                                              onStartNewChatFromMessage(message.id)
                                                        : undefined
                                                }
                                                messageMarkerProps={messageMarkerProps}
                                                canExportData={
                                                    user != null &&
                                                    user.isUnaffiliated === false &&
                                                    hasExportableQuery
                                                }
                                                timestamp={message.timestamp}
                                            />
                                        </>
                                    )}

                                {message.role === ROLE_TYPES.ASSISTANT &&
                                    isLatestAssistantMessage &&
                                    !isRevealing &&
                                    chatState === CHAT_STATES.IDLE && (
                                        <Avatar
                                            size="sm"
                                            icon={<Icon icon="lucide:bot" width={16} height={16} />}
                                            className="mt-4 flex-shrink-0 bg-mfour-green text-primary-foreground"
                                        />
                                    )}
                            </div>
                        </div>
                    </motion.div>
                )}
            </div>
        </AnimatePresence>
    );
};
